import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Modal } from 'src/components/Modal/Modal';
import { convertDate, formatFieldString, shortenString } from 'src/components/Table/helpers';
import { formatCaseUserName } from 'src/api/adminCompliance';
import { ROUTE_USERS } from 'src/utils/routes';

import {
  Breadcrumb,
  Card,
  CardTitle,
  DetailGrid,
  DetailItem,
  DetailLabel,
  DetailValue,
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
  UserLink,
} from 'src/pages/Transactions/styled';
import CopyableValue from 'src/pages/Transactions/CopyableValue';

type OrderOwner = {
  id?: string;
  name?: { first?: string; last?: string } | null;
  email?: string | null;
  bxlend_id?: string | null;
};

type OrderDetailModalProps = {
  order: Record<string, any>;
  onClose: () => void;
};

const pick = (...values: unknown[]) =>
  values.find((v) => v !== undefined && v !== null && v !== '') as string | number | undefined;

const formatDecimal = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'object' && value !== null && '$numberDecimal' in (value as object)) {
    return String((value as { $numberDecimal?: string }).$numberDecimal ?? '');
  }
  return String(value);
};

const SectionSpacer = () => <div style={{ height: '1rem' }} />;


const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ order, onClose }) => {
  const navigate = useNavigate();

  const owner = (order.owner || null) as OrderOwner | null;
  const ownerId = owner?.id;
  const ownerName = formatCaseUserName(owner);
  const ownerEmail = owner?.email || undefined;

  const id = String(pick(order.id, order._id) ?? '');
  const direction = order.direction ? String(order.direction) : '';
  const pair = order.pair ? String(order.pair) : '';
  const kind = order.kind ? String(order.kind) : '';
  const status = order.status ? String(order.status) : '';
  const amount = formatDecimal(order.amount);
  const quantity = formatDecimal(order.quantity);
  const executedPrice = formatDecimal(order.executed_price);
  const feePct = pick(order.fee?.percentage);
  const feeAmount = formatDecimal(order.fee?.amount);
  const createdAt = order.created_at as string | undefined;
  const updatedAt = order.updated_at as string | undefined;

  const provider = order.provider_info;
  const providerPrice = formatDecimal(provider?.executed_price);
  const providerAmount = formatDecimal(provider?.amount);

  const headline =
    [direction ? formatFieldString(direction) : null, pair || null].filter(Boolean).join(' · ') ||
    (kind ? formatFieldString(kind) : 'Order');

  const createdMeta = createdAt
    ? `Created ${convertDate(createdAt).date}${
        convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''
      }`
    : null;

  const feeLabel = [
    feePct != null && feePct !== '' ? `${feePct}%` : null,
    feeAmount || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Modal isOpen onClose={onClose}>
      <Breadcrumb>Orders{id ? ` / ${shortenString(id)}` : ''}</Breadcrumb>

      <HeadlineRow>
        <TitleGroup>
          <Title>
            {headline}
            {status ? (
              <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
            ) : null}
          </Title>
          {createdMeta ? <MetaLine>{createdMeta}</MetaLine> : null}
        </TitleGroup>
      </HeadlineRow>

      {(ownerName || ownerId || ownerEmail) && (
        <>
          <Card>
            <CardTitle>User</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Name</DetailLabel>
                {ownerId ? (
                  <DetailValue title={ownerEmail ? `${ownerName} (${ownerEmail})` : ownerName}>
                    <UserLink
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`${ROUTE_USERS}/${ownerId}`);
                      }}
                    >
                      {ownerName && ownerName !== '—' ? ownerName : shortenString(ownerId)}
                    </UserLink>
                  </DetailValue>
                ) : (
                  <DetailValue>{ownerName || '—'}</DetailValue>
                )}
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue $muted={!ownerEmail}>{ownerEmail || '—'}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>BxLend ID</DetailLabel>
                <DetailValue $muted={!owner?.bxlend_id}>{owner?.bxlend_id || '—'}</DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
          <SectionSpacer />
        </>
      )}

      <Card>
        <CardTitle>Order</CardTitle>
        <DetailGrid>
          {id ? (
            <DetailItem>
              <DetailLabel>Order ID</DetailLabel>
              <CopyableValue value={id} label="Order ID" />
            </DetailItem>
          ) : null}
          <DetailItem>
            <DetailLabel>Direction</DetailLabel>
            <DetailValue $muted={!direction}>
              {direction ? formatFieldString(direction) : '—'}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Pair</DetailLabel>
            <DetailValue $muted={!pair}>{pair || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Kind</DetailLabel>
            <DetailValue $muted={!kind}>{kind ? formatFieldString(kind) : '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              {status ? (
                <StatusBadge $status={status}>{formatFieldString(status)}</StatusBadge>
              ) : (
                '—'
              )}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Amount</DetailLabel>
            <DetailValue $muted={!amount}>{amount || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Quantity</DetailLabel>
            <DetailValue $muted={!quantity}>{quantity || '—'}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Executed price</DetailLabel>
            <DetailValue $muted={!executedPrice}>{executedPrice || '—'}</DetailValue>
          </DetailItem>
          {feeLabel ? (
            <DetailItem>
              <DetailLabel>Fee</DetailLabel>
              <DetailValue>{feeLabel}</DetailValue>
            </DetailItem>
          ) : null}
          {createdAt ? (
            <DetailItem>
              <DetailLabel>Created</DetailLabel>
              <DetailValue>
                {convertDate(createdAt).date}
                {convertDate(createdAt).time ? ` · ${convertDate(createdAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
          {updatedAt ? (
            <DetailItem>
              <DetailLabel>Updated</DetailLabel>
              <DetailValue>
                {convertDate(updatedAt).date}
                {convertDate(updatedAt).time ? ` · ${convertDate(updatedAt).time}` : ''}
              </DetailValue>
            </DetailItem>
          ) : null}
        </DetailGrid>
      </Card>

      {provider?.platform ? (
        <>
          <SectionSpacer />
          <Card>
            <CardTitle>Provider</CardTitle>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Platform</DetailLabel>
                <DetailValue>{provider.platform}</DetailValue>
              </DetailItem>
              {provider.id ? (
                <DetailItem>
                  <DetailLabel>Provider ID</DetailLabel>
                  <CopyableValue value={String(provider.id)} shorten={false} label="Provider ID" />
                </DetailItem>
              ) : null}
              {provider.market ? (
                <DetailItem>
                  <DetailLabel>Market</DetailLabel>
                  <DetailValue>{provider.market}</DetailValue>
                </DetailItem>
              ) : null}
              {provider.type ? (
                <DetailItem>
                  <DetailLabel>Type</DetailLabel>
                  <DetailValue>{formatFieldString(String(provider.type))}</DetailValue>
                </DetailItem>
              ) : null}
              {providerPrice ? (
                <DetailItem>
                  <DetailLabel>Executed price</DetailLabel>
                  <DetailValue>{providerPrice}</DetailValue>
                </DetailItem>
              ) : null}
              {providerAmount ? (
                <DetailItem>
                  <DetailLabel>Amount</DetailLabel>
                  <DetailValue>{providerAmount}</DetailValue>
                </DetailItem>
              ) : null}
              {provider.datetime ? (
                <DetailItem>
                  <DetailLabel>Datetime</DetailLabel>
                  <DetailValue>
                    {convertDate(provider.datetime).date}
                    {convertDate(provider.datetime).time
                      ? ` · ${convertDate(provider.datetime).time}`
                      : ''}
                  </DetailValue>
                </DetailItem>
              ) : null}
            </DetailGrid>
          </Card>
        </>
      ) : null}
    </Modal>
  );
};

export default OrderDetailModal;
